import { useEffect, useId, useRef, useState } from 'react';
import type { SpaceDefinition, SpaceId } from '@respaldos/shared';
import { PRODUCT_NAME, PRODUCT_SUB } from './AuthGate';
import { IconClock, IconFolder, IconServer, IconUsers } from './Icons';
import type { SessionUser } from '../lib/api';

export type TopSection = 'files' | 'activity' | 'users';

export type NavSelection = SpaceId | 'activity' | 'users';

interface Props {
  spaces: SpaceDefinition[];
  selected: NavSelection;
  onSelect: (id: NavSelection) => void;
  isAdmin: boolean;
}

export function TopBar({
  user,
  section,
  onSection,
  isAdmin,
  onLogout,
}: {
  user: SessionUser;
  section: TopSection;
  onSection: (section: TopSection) => void;
  isAdmin: boolean;
  onLogout: () => void | Promise<void>;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!menuOpen) return;
    function onPointerDown(event: PointerEvent) {
      if (!accountRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  function signOut() {
    setMenuOpen(false);
    void Promise.resolve(onLogout()).finally(() => {
      window.location.reload();
    });
  }

  return (
    <header className="topbar">
      <div className="topbar-brand">
        <span className="topbar-logo-wrap">
          <img
            className="topbar-logo"
            src="/brand/grupo-galero-logo.svg"
            alt="Grupo Galero"
          />
        </span>
        <div className="topbar-copy">
          <div className="topbar-product">{PRODUCT_NAME}</div>
          <div className="topbar-sub">{PRODUCT_SUB}</div>
        </div>
      </div>

      <nav className="topbar-nav" aria-label="Principal">
        <button
          type="button"
          className={`nav-link ${section === 'files' ? 'active' : ''}`}
          onClick={() => onSection('files')}
        >
          Archivos
        </button>
        <button
          type="button"
          className={`nav-link ${section === 'activity' ? 'active' : ''}`}
          onClick={() => onSection('activity')}
        >
          Actividad
        </button>
        {isAdmin && (
          <button
            type="button"
            className={`nav-link ${section === 'users' ? 'active' : ''}`}
            onClick={() => onSection('users')}
          >
            Usuarios
          </button>
        )}
      </nav>

      <div className="topbar-account" ref={accountRef}>
        <button
          type="button"
          className="account-chip"
          title="Cuenta"
          aria-label={`Cuenta de ${user.name}`}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls={menuId}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span className="account-avatar" aria-hidden="true">
            {user.initials}
          </span>
          <span className="account-meta">
            <span className="account-name">{user.name}</span>
            <span className="account-role">{user.primaryGroup}</span>
          </span>
        </button>
        {menuOpen && (
          <div className="account-menu" id={menuId} role="menu">
            <div className="account-menu-id">
              <div className="account-menu-name">{user.name}</div>
              {user.primaryGroup ? (
                <div className="account-menu-group">{user.primaryGroup}</div>
              ) : null}
            </div>
            <button
              type="button"
              className="account-menu-item"
              role="menuitem"
              onClick={signOut}
            >
              Cerrar sesión
            </button>
          </div>
        )}
      </div>
    </header>
  );
}

export function Sidebar({
  spaces,
  selected,
  onSelect,
  isAdmin,
}: Pick<Props, 'spaces' | 'selected' | 'onSelect' | 'isAdmin'>) {
  return (
    <aside className="sidebar">
      <div className="side-label">Espacios</div>
      {spaces.map((space) => (
        <button
          key={space.id}
          type="button"
          className={`nav-item ${selected === space.id ? 'active' : ''}`}
          onClick={() => onSelect(space.id)}
        >
          {space.kind === 'marketing' ? <IconFolder /> : <IconServer />}
          {space.label}
          {space.hint && <span className="hint">{space.hint}</span>}
        </button>
      ))}
      <div className="side-divider" />
      <div className="side-label">Auditoría</div>
      <button
        type="button"
        className={`nav-item ${selected === 'activity' ? 'active' : ''}`}
        onClick={() => onSelect('activity')}
      >
        <IconClock />
        Actividad
      </button>
      {isAdmin && (
        <>
          <div className="side-divider" />
          <div className="side-label">Administración</div>
          <button
            type="button"
            className={`nav-item ${selected === 'users' ? 'active' : ''}`}
            onClick={() => onSelect('users')}
          >
            <IconUsers />
            Usuarios
          </button>
        </>
      )}
    </aside>
  );
}
